import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  InsertChart as InsertChartIcon,
  School as SchoolIcon,
  Equalizer as EqualizerIcon,
  Compare as CompareIcon,
  Download as DownloadIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import AdminLayout from './components/AdminLayout';
import * as reportService from '../../services/reportService';
import { 
  SessionPerformance, 
  StudentPerformance,
  ProblemTypeDistribution,
  DifficultyDistribution,
  AchievementData
} from '../../services/reportService';

// Define interfaces for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
      style={{ marginTop: 16 }}
    >
      {value === index && children}
    </div>
  );
};

// Session Performance Report Component
function SessionPerformanceReportPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionPerformance[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await reportService.getSessionPerformanceReport();
        setSessionData(data);
      } catch (err) {
        console.error('Error fetching session performance:', err);
        setError('Failed to load session performance data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (sessionData.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No session performance data available yet.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'primary.main' }}>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Session Title</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Duration</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Students</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Problems</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Avg. Score</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Completion Rate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessionData.map((session) => (
            <TableRow key={session.id} hover>
              <TableCell component="th" scope="row">
                {session.title}
              </TableCell>
              <TableCell align="center">
                {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
              </TableCell>
              <TableCell align="center">{session.totalStudents}</TableCell>
              <TableCell align="center">{session.totalProblems}</TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: '40px' }}>
                    {session.averageScore}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={session.averageScore} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4, 
                      ml: 2, 
                      flexGrow: 1,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: session.averageScore > 75 ? 'success.main' : 
                                         session.averageScore > 50 ? 'warning.main' : 'error.main',
                      }
                    }} 
                  />
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ minWidth: '40px' }}>
                    {session.completionRate}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={session.completionRate} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4, 
                      ml: 2, 
                      flexGrow: 1,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: session.completionRate > 75 ? 'success.main' : 
                                         session.completionRate > 50 ? 'warning.main' : 'error.main',
                      }
                    }} 
                  />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Top Students Report Component
function TopStudentsReportPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [studentsData, setStudentsData] = useState<StudentPerformance[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await reportService.getTopStudentsReport();
        setStudentsData(data);
      } catch (err) {
        console.error('Error fetching top students data:', err);
        setError('Failed to load top students data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (studentsData.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No student performance data available yet.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'primary.main' }}>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Student Name</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Problems Solved</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Accuracy</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Total Attempts</TableCell>
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Sessions Joined</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {studentsData.map((student, index) => (
            <TableRow 
              key={student.id} 
              hover
              sx={{
                backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.05)' : 'inherit',
              }}
            >
              <TableCell component="th" scope="row">
                {index < 3 && (
                  <Typography 
                    variant="body2" 
                    component="span" 
                    sx={{
                      mr: 1,
                      fontWeight: 'bold',
                      color: index === 0 ? 'gold' : index === 1 ? 'silver' : '#CD7F32', // gold, silver, bronze
                    }}
                  >
                    #{index + 1}
                  </Typography>
                )}
                {student.name}
              </TableCell>
              <TableCell align="center">{student.problemsSolved}</TableCell>
              <TableCell align="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'medium',
                    color: student.accuracy > 85 ? 'success.main' : 
                           student.accuracy > 65 ? 'warning.main' : 'error.main'
                  }}
                >
                  {student.accuracy}%
                </Typography>
              </TableCell>
              <TableCell align="center">{student.totalAttempts}</TableCell>
              <TableCell align="center">{student.sessionsParticipated}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Problem Distribution Report Component
function ProblemDistributionPanel() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeData, setTypeData] = useState<ProblemTypeDistribution[]>([]);
  const [difficultyData, setDifficultyData] = useState<DifficultyDistribution[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [typeDistribution, diffDistribution] = await Promise.all([
          reportService.getProblemTypeDistribution(),
          reportService.getDifficultyDistribution()
        ]);
        setTypeData(typeDistribution);
        setDifficultyData(diffDistribution);
      } catch (err) {
        console.error('Error fetching problem distribution data:', err);
        setError('Failed to load problem distribution data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (typeData.length === 0 || difficultyData.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No problem distribution data available yet.
      </Alert>
    );
  }

  // Get color for difficulty level
  const getDifficultyColor = (level: number) => {
    switch (level) {
      case 1: return theme.palette.success.light;
      case 2: return theme.palette.success.main;
      case 3: return theme.palette.warning.main;
      case 4: return theme.palette.error.light;
      case 5: return theme.palette.error.main;
      default: return theme.palette.primary.main;
    }
  };

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {/* Problem Type Distribution */}
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardHeader title="Problem Type Distribution" />
          <Divider />
          <CardContent>
            {typeData.map((item) => (
              <Box key={item.category} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{item.category}</Typography>
                  <Typography variant="body2">
                    {item.count} problems ({item.percentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.percentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                  }}
                />
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      {/* Problem Difficulty Distribution */}
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardHeader title="Problem Difficulty Distribution" />
          <Divider />
          <CardContent>
            {difficultyData.map((item) => (
              <Box key={item.level} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Level {item.level}</Typography>
                  <Typography variant="body2">
                    {item.count} problems ({item.percentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.percentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getDifficultyColor(item.level),
                    }
                  }}
                />
              </Box>
            ))}
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'success.main' }}>
                Level 1-2: Easy
              </Typography>
              <Typography variant="caption" sx={{ color: 'warning.main' }}>
                Level 3: Medium
              </Typography>
              <Typography variant="caption" sx={{ color: 'error.main' }}>
                Level 4-5: Hard
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// Session Comparison Panel Component
function SessionComparisonPanel() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionPerformance[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<number[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await reportService.getSessionComparison();
        
        // The data is already of type SessionPerformance[] from our service
        setSessionData(data);
        
        // Pre-select first two sessions if available
        if (data.length >= 2) {
          setSelectedSessions([data[0].id, data[1].id]);
        } else if (data.length === 1) {
          setSelectedSessions([data[0].id]);
        }
      } catch (err) {
        console.error('Error fetching session comparison data:', err);
        setError('Failed to load session comparison data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (sessionData.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No session data available for comparison.
      </Alert>
    );
  }

  // Filter selected sessions for comparison
  const comparisonData = sessionData.filter(session => 
    selectedSessions.includes(session.id)
  );

  return (
    <Box>
      {/* Session Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select Sessions to Compare:
        </Typography>
        <Grid container spacing={2}>
          {sessionData.map(session => (
            <Grid item key={session.id}>
              <Button
                variant={selectedSessions.includes(session.id) ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => {
                  const currentIndex = selectedSessions.indexOf(session.id);
                  if (currentIndex === -1 && selectedSessions.length < 3) {
                    // Add the session if not already selected and less than 3 are selected
                    setSelectedSessions([...selectedSessions, session.id]);
                  } else if (currentIndex !== -1) {
                    // Remove the session if already selected
                    const newSelectedSessions = [...selectedSessions];
                    newSelectedSessions.splice(currentIndex, 1);
                    setSelectedSessions(newSelectedSessions);
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                {session.title}
              </Button>
            </Grid>
          ))}
        </Grid>
        
        {selectedSessions.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please select at least one session to view comparison data.
          </Alert>
        )}
      </Paper>

      {/* Comparison Charts - only shown when sessions are selected */}
      {selectedSessions.length > 0 && (
        <Grid container spacing={3}>
          {/* Student Count Comparison */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardHeader title="Students Enrolled" />
              <Divider />
              <CardContent>
                {comparisonData.map(session => (
                  <Box key={session.id} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Tooltip title={`${new Date(session.startDate).toLocaleDateString()} - ${new Date(session.endDate).toLocaleDateString()}`}>
                        <Typography variant="body2">{session.title}</Typography>
                      </Tooltip>
                      <Typography variant="body2" fontWeight="bold">
                        {session.totalStudents} students
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={session.totalStudents > 100 ? 100 : session.totalStudents}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.primary.main,
                        }
                      }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Score Comparison */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardHeader title="Average Score" />
              <Divider />
              <CardContent>
                {comparisonData.map(session => (
                  <Box key={session.id} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{session.title}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {session.averageScore}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={session.averageScore}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: session.averageScore > 75 ? theme.palette.success.main : 
                                        session.averageScore > 50 ? theme.palette.warning.main : theme.palette.error.main,
                        }
                      }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Completion Rate Comparison */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardHeader title="Completion Rate" />
              <Divider />
              <CardContent>
                {comparisonData.map(session => (
                  <Box key={session.id} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{session.title}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {session.completionRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={session.completionRate}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: session.completionRate > 75 ? theme.palette.success.main : 
                                        session.completionRate > 50 ? theme.palette.warning.main : theme.palette.error.main,
                        }
                      }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Problems Count Comparison */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardHeader title="Problems Count" />
              <Divider />
              <CardContent>
                {comparisonData.map(session => (
                  <Box key={session.id} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{session.title}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {session.totalProblems} problems
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={session.totalProblems > 50 ? 100 : session.totalProblems * 2}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.secondary.main,
                        }
                      }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}


// Achievement Analysis Panel
function AchievementAnalysisPanel() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [achievementData, setAchievementData] = useState<AchievementData[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await reportService.getAchievementDistribution();
        setAchievementData(data);
      } catch (err) {
        console.error('Error fetching achievement data:', err);
        setError('Failed to load achievement distribution data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (achievementData.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No achievement data available yet.
      </Alert>
    );
  }

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {/* Achievement Distribution Chart */}
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardHeader title="Achievement Distribution" />
          <Divider />
          <CardContent>
            {achievementData.map((item, index) => (
              <Box key={item.name} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">
                    {index < 3 && (
                      <Typography 
                        variant="body2" 
                        component="span" 
                        sx={{
                          mr: 1,
                          fontWeight: 'bold',
                          color: index === 0 ? 'gold' : index === 1 ? 'silver' : '#CD7F32', // gold, silver, bronze
                        }}
                      >
                        ★
                      </Typography>
                    )}
                    {item.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{item.count}</strong> students ({item.percentage}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.percentage > 100 ? 100 : item.percentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: index === 0 ? 'gold' : 
                                   index === 1 ? 'silver' : 
                                   index === 2 ? '#CD7F32' : theme.palette.primary.main,
                    }
                  }}
                />
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      {/* Achievement Analysis Summary */}
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardHeader title="Achievement Analysis Summary" />
          <Divider />
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Most Earned Achievement</Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(255, 215, 0, 0.1)' }}>
                <Typography variant="h6">{achievementData[0]?.name || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Earned by {achievementData[0]?.count || 0} students ({achievementData[0]?.percentage || 0}%)
                </Typography>
                <Typography variant="body2">
                  This is the most commonly earned achievement, suggesting that the criteria for this achievement might be well-balanced and achievable for students.
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Least Earned Achievement</Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                <Typography variant="h6">{achievementData[achievementData.length - 1]?.name || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Earned by only {achievementData[achievementData.length - 1]?.count || 0} students ({achievementData[achievementData.length - 1]?.percentage || 0}%)
                </Typography>
                <Typography variant="body2">
                  This achievement is rarely earned, which may indicate that its criteria are too challenging or specific. Consider reviewing this achievement to ensure it's attainable.
                </Typography>
              </Paper>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>Recommendations</Typography>
              <ul style={{ paddingLeft: theme.spacing(2), marginTop: 0 }}>
                <li>
                  <Typography variant="body2" paragraph>
                    Consider creating more achievements for mathematics topics with low engagement to increase student motivation.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" paragraph>
                    Review the criteria for rarely earned achievements to ensure they're challenging but attainable.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Create more progressive achievements that build on each other to guide student learning paths.
                  </Typography>
                </li>
              </ul>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// Main Reports Page
function ReportsPage() {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Analytics & Reports
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View performance analytics and generate reports for your MathWiz League platform.
          </Typography>
        </Box>
        
        <Tooltip title="Export Report as CSV">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ height: 40 }}
          >
            Export
          </Button>
        </Tooltip>
      </Box>

      {/* Report Tabs */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }} data-demo-id="admin-reports-panel">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }}
        >
          <Tab 
            label="Session Performance" 
            icon={<InsertChartIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Top Students" 
            icon={<SchoolIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Problem Distribution" 
            icon={<EqualizerIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Session Comparison" 
            icon={<CompareIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Achievement Analysis" 
            icon={<TrophyIcon />} 
            iconPosition="start" 
          />
        </Tabs>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            <SessionPerformanceReportPanel />
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            <TopStudentsReportPanel />
          </TabPanel>
          
          <TabPanel value={activeTab} index={2}>
            <ProblemDistributionPanel />
          </TabPanel>
          
          <TabPanel value={activeTab} index={3}>
            <SessionComparisonPanel />
          </TabPanel>
          
          
          <TabPanel value={activeTab} index={4}>
            <AchievementAnalysisPanel />
          </TabPanel>
        </Box>
      </Paper>
    </AdminLayout>
  );
}

export default ReportsPage;